import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  DollarSign, ChevronLeft, Download, Calendar, TrendingUp, TrendingDown, 
  PieChart, BarChart3, RefreshCw, FileText, AlertCircle, Clock, XCircle,
  ChevronRight, Filter, Search, Building2, Package
} from "lucide-react";
import axios from "axios";
import { SiteSelector, CommercialProvider, useSite } from "./SiteSelector";

const API = '/api';

const PRESETS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "this_month", label: "This Month" },
  { value: "this_year", label: "This Year" },
  { value: "custom", label: "Custom Range" }
];

const FinanceContent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedSite, sites } = useSite();
  
  const [activeTab, setActiveTab] = useState("summary");
  const [summary, setSummary] = useState(null);
  const [bySite, setBySite] = useState(null);
  const [byProduct, setByProduct] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [pending, setPending] = useState(null);
  const [failed, setFailed] = useState(null);
  const [expired, setExpired] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState(() => {
    // Initialize from URL param if valid, otherwise default to this_month
    const urlPreset = new URLSearchParams(window.location.search).get("preset");
    const validPresets = ["today", "yesterday", "this_week", "this_month", "this_year", "custom"];
    return urlPreset && validPresets.includes(urlPreset) ? urlPreset : "this_month";
  });
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  useEffect(() => { 
    loadData(); 
  }, [selectedSite, preset, dateFrom, dateTo, activeTab, page]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("preset", preset);
      if (selectedSite !== "all") params.set("site_id", selectedSite);
      if (preset === "custom" && dateFrom) params.set("date_from", dateFrom);
      if (preset === "custom" && dateTo) params.set("date_to", dateTo);

      // Always load summary
      const summaryRes = await axios.get(`${API}/commercial/finance/revenue/summary?${params}`);
      setSummary(summaryRes.data);

      // Load tab-specific data
      if (activeTab === "by-site") {
        const res = await axios.get(`${API}/commercial/finance/revenue/by-site?${params}`);
        setBySite(res.data);
      } else if (activeTab === "by-product") {
        const res = await axios.get(`${API}/commercial/finance/revenue/by-product?${params}`);
        setByProduct(res.data);
      } else if (activeTab === "transactions") {
        params.set("page", page.toString());
        if (statusFilter) params.set("status", statusFilter);
        if (searchTerm) params.set("search", searchTerm);
        const res = await axios.get(`${API}/commercial/finance/transactions?${params}`);
        setTransactions(res.data);
      } else if (activeTab === "pending") {
        params.set("page", page.toString());
        const res = await axios.get(`${API}/commercial/finance/payments/pending?${params}`);
        setPending(res.data);
      } else if (activeTab === "failed") {
        params.set("page", page.toString());
        const res = await axios.get(`${API}/commercial/finance/payments/failed?${params}`);
        setFailed(res.data);
      } else if (activeTab === "expired") {
        params.set("page", page.toString());
        const res = await axios.get(`${API}/commercial/finance/payments/expired?${params}`);
        setExpired(res.data);
      }
    } catch (err) { 
      console.error("Finance load error:", err); 
    } finally { 
      setLoading(false); 
    }
  };

  const exportCSV = async () => {
    try {
      const params = new URLSearchParams();
      params.set("preset", preset);
      if (selectedSite !== "all") params.set("site_id", selectedSite);
      if (statusFilter) params.set("status", statusFilter);
      
      window.open(`${API}/commercial/finance/export/csv?${params}`, "_blank");
    } catch (err) {
      console.error("Export error:", err);
    }
  };

  const exportXLSX = async () => {
    try {
      const params = new URLSearchParams();
      params.set("preset", preset);
      if (selectedSite !== "all") params.set("site_id", selectedSite);
      if (statusFilter) params.set("status", statusFilter);
      
      window.open(`${API}/commercial/finance/export/xlsx?${params}`, "_blank");
    } catch (err) {
      console.error("Export error:", err);
    }
  };

  const exportVATReport = async () => {
    try {
      const params = new URLSearchParams();
      params.set("preset", preset);
      if (selectedSite !== "all") params.set("site_id", selectedSite);
      
      window.open(`${API}/commercial/finance/export/vat-report?${params}`, "_blank");
    } catch (err) {
      console.error("Export error:", err);
    }
  };

  const currentSite = sites.find(s => s.site_id === selectedSite);

  const tabs = [
    { id: "summary", label: "Summary", icon: PieChart },
    { id: "by-site", label: "By Site", icon: Building2 },
    { id: "by-product", label: "By Product", icon: Package },
    { id: "transactions", label: "Transactions", icon: FileText },
    { id: "pending", label: "Pending", icon: Clock },
    { id: "failed", label: "Failed", icon: XCircle },
    { id: "expired", label: "Expired", icon: AlertCircle }
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="commercial-finance-centre">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin/dashboard")} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="back-btn">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Finance Centre</h1>
              <p className="text-sm text-gray-500">Revenue, VAT & transaction analytics</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <SiteSelector />
            <button onClick={exportCSV} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50" data-testid="export-csv-btn">
              <Download className="w-4 h-4" /> CSV
            </button>
            <button onClick={exportXLSX} className="flex items-center gap-2 px-3 py-2 text-sm border border-green-500 text-green-600 rounded-lg hover:bg-green-50" data-testid="export-xlsx-btn">
              <Download className="w-4 h-4" /> Excel
            </button>
            <button onClick={exportVATReport} className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-testid="export-vat-btn">
              <FileText className="w-4 h-4" /> VAT Report
            </button>
          </div>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center gap-4">
          <Calendar className="w-4 h-4 text-gray-400" />
          <select 
            value={preset} 
            onChange={(e) => setPreset(e.target.value)} 
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            data-testid="preset-select"
          >
            {PRESETS.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          {preset === "custom" && (
            <>
              <input 
                type="date" 
                value={dateFrom} 
                onChange={(e) => setDateFrom(e.target.value)} 
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                data-testid="date-from"
              />
              <span className="text-gray-500">to</span>
              <input 
                type="date" 
                value={dateTo} 
                onChange={(e) => setDateTo(e.target.value)} 
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                data-testid="date-to"
              />
            </>
          )}
          <button onClick={loadData} className="px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg flex items-center gap-1">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setPage(1); }}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id 
                  ? "border-blue-600 text-blue-600" 
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        ) : (
          <>
            {/* Summary Tab */}
            {activeTab === "summary" && summary && (
              <div className="space-y-6">
                {/* VAT Notice */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-blue-800">VAT Rate: {summary.vat_rate_display}</p>
                      <p className="text-xs text-blue-600">
                        {selectedSite !== "all" && currentSite ? `Configured for ${currentSite.name}` : "Platform default rate"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="card-gross">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-500">Gross Revenue</span>
                      <TrendingUp className="w-5 h-5 text-green-500" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{summary.totals?.gross_display}</p>
                    <p className="text-xs text-gray-400 mt-1">{summary.totals?.transaction_count} transactions</p>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="card-vat">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-500">VAT ({summary.vat_rate_display})</span>
                      <FileText className="w-5 h-5 text-blue-500" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{summary.totals?.vat_display}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="card-net">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-500">Net Revenue</span>
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <p className="text-2xl font-bold text-green-600">{summary.totals?.net_display}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="card-trend">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-500">{summary.comparison?.comparison_label || "vs Previous Period"}</span>
                      {summary.comparison?.trend_direction === "up" ? (
                        <TrendingUp className="w-5 h-5 text-green-500" />
                      ) : (
                        <TrendingDown className="w-5 h-5 text-red-500" />
                      )}
                    </div>
                    <p className={`text-2xl font-bold ${summary.comparison?.trend_pct >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {summary.comparison?.trend_pct >= 0 ? "+" : ""}{summary.comparison?.trend_pct}%
                    </p>
                    <p className="text-xs text-gray-400 mt-1">{summary.comparison?.comparison_label || "Previous"}: {summary.comparison?.previous_period_gross_display}</p>
                  </div>
                </div>

                {/* Revenue Breakdown */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">Revenue Breakdown</h3>
                  <div className="space-y-4">
                    {Object.entries(summary.breakdown || {}).map(([key, data]) => (
                      <div 
                        key={key}
                        onClick={() => data.drill_down && navigate(data.drill_down)} 
                        className="cursor-pointer hover:bg-gray-50 p-4 rounded-lg border border-gray-100"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">{key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-bold text-gray-900">{data.gross_display}</span>
                            <span className="text-xs text-gray-400">({data.count} txns)</span>
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-500 h-2 rounded-full" 
                            style={{width: `${summary.totals?.gross_pence > 0 ? (data.gross_pence / summary.totals.gross_pence * 100) : 0}%`}}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* By Site Tab */}
            {activeTab === "by-site" && bySite && (
              <div className="bg-white rounded-xl border border-gray-200">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900">Revenue by Site</h3>
                  <p className="text-sm text-gray-500">Total: {bySite.total_gross_display}</p>
                </div>
                <div className="divide-y divide-gray-100">
                  {bySite.sites?.map(site => (
                    <div 
                      key={site.site_id} 
                      onClick={() => navigate(site.drill_down)}
                      className="p-4 hover:bg-gray-50 cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-medium text-gray-900">{site.site_name}</p>
                          <p className="text-xs text-gray-500">{site.site_code} • {site.transaction_count} transactions</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">{site.gross_display}</p>
                          <p className="text-xs text-gray-500">{site.percentage}%</p>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{width: `${site.percentage}%`}} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* By Product Tab */}
            {activeTab === "by-product" && byProduct && (
              <div className="bg-white rounded-xl border border-gray-200">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900">Revenue by Product Type</h3>
                  <p className="text-sm text-gray-500">Total: {byProduct.total_gross_display}</p>
                </div>
                <div className="divide-y divide-gray-100">
                  {byProduct.products?.map(product => (
                    <div key={product.product_type} className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-medium text-gray-900">{product.product_name}</p>
                          <p className="text-xs text-gray-500">{product.count} purchases</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">{product.gross_display}</p>
                          <p className="text-xs text-gray-500">{product.percentage}%</p>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-purple-500 h-2 rounded-full" style={{width: `${product.percentage}%`}} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Transactions Tab */}
            {activeTab === "transactions" && transactions && (
              <div className="space-y-4">
                {/* Filters */}
                <div className="flex items-center gap-4">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && loadData()}
                      placeholder="Search by reg number or receipt..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
                      data-testid="transaction-search"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    data-testid="status-filter"
                  >
                    <option value="">All Statuses</option>
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>

                {/* Table */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Payment ID</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Reg Number</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Amount</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Site</th>
                        <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {transactions.transactions?.map(t => (
                        <tr 
                          key={t.payment_id} 
                          onClick={() => navigate(`/admin/commercial/payments/${t.payment_id}`)}
                          className="hover:bg-gray-50 cursor-pointer"
                        >
                          <td className="px-4 py-3 font-mono text-xs">{t.payment_id}</td>
                          <td className="px-4 py-3 font-medium">{t.reg_number}</td>
                          <td className="px-4 py-3 font-medium">{t.amount_display}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              t.status === "completed" ? "bg-green-100 text-green-700" :
                              t.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                              t.status === "failed" ? "bg-red-100 text-red-700" :
                              "bg-gray-100 text-gray-700"
                            }`}>
                              {t.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-500">{t.site_name}</td>
                          <td className="px-4 py-3 text-gray-500">{new Date(t.created_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {transactions.pages > 1 && (
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500">
                      Page {transactions.page} of {transactions.pages} ({transactions.total} total)
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setPage(p => Math.min(transactions.pages, p + 1))}
                        disabled={page === transactions.pages}
                        className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Pending Tab */}
            {activeTab === "pending" && pending && (
              <div className="bg-white rounded-xl border border-gray-200">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900">Pending Payments</h3>
                  <p className="text-sm text-gray-500">{pending.total} awaiting completion</p>
                </div>
                {pending.payments?.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <Clock className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No pending payments</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {pending.payments?.map(p => (
                      <div key={p.payment_id} className="p-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{p.reg_number}</p>
                            <p className="text-xs text-gray-500">{p.payment_id}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold">{p.amount_display}</p>
                            <p className="text-xs text-yellow-600">
                              {p.expires_in > 0 ? `Expires in ${Math.round(p.expires_in / 60)} min` : "Expiring soon"}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Failed Tab */}
            {activeTab === "failed" && failed && (
              <div className="bg-white rounded-xl border border-gray-200">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900">Failed Payments</h3>
                  <p className="text-sm text-gray-500">{failed.total} failed attempts</p>
                </div>
                {failed.payments?.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <XCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No failed payments in this period</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {failed.payments?.map(p => (
                      <div key={p.payment_id} className="p-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{p.reg_number}</p>
                            <p className="text-xs text-gray-500">{new Date(p.created_at).toLocaleString()}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-red-600">{p.amount_display}</p>
                            <p className="text-xs text-red-500">{p.failure_reason || "Payment failed"}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Expired Tab */}
            {activeTab === "expired" && expired && (
              <div className="bg-white rounded-xl border border-gray-200">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900">Expired Sessions</h3>
                  <p className="text-sm text-gray-500">{expired.total} expired checkout sessions</p>
                </div>
                {expired.payments?.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <AlertCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No expired sessions in this period</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {expired.payments?.map(p => (
                      <div key={p.payment_id} className="p-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{p.reg_number}</p>
                            <p className="text-xs text-gray-500">{new Date(p.created_at).toLocaleString()}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-gray-400">{p.amount_display}</p>
                            <p className="text-xs text-gray-400">Session expired</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default function CommercialFinance() {
  return (
    <CommercialProvider>
      <FinanceContent />
    </CommercialProvider>
  );
}
